import { customElement, property } from "lit/decorators.js";
import { html, TemplateResult, PropertyValues } from "lit";
import "@/components/annotations/annotations_cell";
import "@/components/annotations/hidden_annotations_dot";
import { initTooltips } from "@/utilities";
import { userAnnotationState } from "@/state/UserAnnotations";
import { MachineAnnotation } from "@/state/MachineAnnotations";
import "@/components/annotations/create_annotation_button";
import { triggerSelectionStart } from "@/components/annotations/selectionHelpers";
import "@/components/annotations/line_of_code";
import { DodonaElement } from "@/components/meta/dodona_element";

/**
 * This component represents a row in the code listing.
 * It contains the line number and the code itself, and the button to add a new annotation for this row.
 * It also contains the annotations for this row.
 *
 * @element d-code-listing-row
 *
 * @prop {number} row - The row number.
 * @prop {string} renderedCode - The code to display.
 * @prop {string} code - The raw source code of the line.
 * @prop {boolean} canCreateAnnotation - Whether the current user may create annotations.
 * @prop {boolean} hasMachineAnnotation - Whether this row has machine annotations.
 * @prop {boolean} isQuestionMode - Whether the annotation system is in question mode.
 * @prop {MachineAnnotation[]} machineAnnotationsToMark - Machine annotations to mark on this line.
 */
@customElement("d-code-listing-row")
export class CodeListingRow extends DodonaElement {
    @property({ type: Number })
    row: number;

    @property({ type: String, attribute: "rendered-code" })
    renderedCode: string;

    @property({ type: String })
    code = "";

    @property({ type: Boolean })
    canCreateAnnotation = false;

    @property({ type: Boolean })
    hasMachineAnnotation = false;

    @property({ type: Boolean })
    isQuestionMode = false;

    @property({ attribute: false })
    machineAnnotationsToMark: MachineAnnotation[] = [];

    onPointerDown(e: PointerEvent): void {
        triggerSelectionStart(e);
    }

    firstUpdated(_changedProperties: PropertyValues): void {
        super.firstUpdated(_changedProperties);
        this.addEventListener("pointerdown", this.onPointerDown);
        initTooltips(this);
    }

    protected updated(): void {
        // Tooltips can be added/removed as the row rerenders (buttons/icons appear/disappear)
        initTooltips(this);
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        this.removeEventListener("pointerdown", this.onPointerDown);
    }

    get shouldShowCreateButton(): boolean {
        if (!this.canCreateAnnotation || userAnnotationState.formShown) {
            return false;
        }
        return (
            userAnnotationState.hoveredRow === this.row ||
            userAnnotationState.isDragStartRow(this.row) ||
            userAnnotationState.isRangeEndRow(this.row)
        );
    }

    get formShown(): boolean {
        const range = userAnnotationState.selectedRange;
        return userAnnotationState.formShown && !!range && range.row + range.rows - 1 === this.row;
    }

    /**
     * Whether this line has any annotations. Machine annotations are static after
     * load and arrive as a prop; user annotations are read reactively here so the
     * row re-renders and mounts the (otherwise skipped) annotations cell and
     * hidden-dot when one is added. Most lines have none, so skipping those
     * components is a large per-row saving.
     */
    get hasAnnotations(): boolean {
        const user = userAnnotationState.rootIdsByLine.get(this.row)?.length ?? 0;
        return this.hasMachineAnnotation || user > 0;
    }

    onCloseForm(): void {
        userAnnotationState.formShown = false;
        userAnnotationState.selectedRange = null;
    }

    onDragEnter(e: DragEvent): void {
        if (userAnnotationState.dragStartRow === null) {
            return;
        }

        e.preventDefault();
        const origin = userAnnotationState.dragStartRow;
        const startRow = Math.min(origin, this.row);
        const endRow = Math.max(origin, this.row);

        userAnnotationState.selectedRange = {
            row: startRow,
            rows: endRow - startRow + 1,
        };
    }

    render(): TemplateResult {
        return html`
            <tr id="line-${this.row}" class="lineno" @dragenter=${this.onDragEnter} aria-rowindex=${this.row}>
                <td class="rouge-gutter gl">
                    ${
                        this.shouldShowCreateButton
                            ? html`<d-create-annotation-button
                                  row="${this.row}"
                                  .isQuestionMode=${this.isQuestionMode}
                              ></d-create-annotation-button>`
                            : html``
                    }
                    ${
                        this.hasAnnotations
                            ? html`<d-hidden-annotations-dot .row=${this.row}></d-hidden-annotations-dot>`
                            : html``
                    }
                    <pre style="user-select: none;">${this.row}</pre>
                </td>
                <td class="rouge-code">
                    <d-line-of-code
                        .row=${this.row}
                        .code=${this.code}
                        .machineAnnotationsToMark=${this.machineAnnotationsToMark}
                        .renderedCode=${this.renderedCode}
                    ></d-line-of-code>
                    ${
                        this.hasAnnotations || this.formShown
                            ? html`<d-annotations-cell
                                  .row=${this.row}
                                  .formShown=${this.formShown}
                                  @close-form=${this.onCloseForm}
                              ></d-annotations-cell>`
                            : html``
                    }
                </td>
            </tr>
        `;
    }
}
